import React, { useState, useEffect, useRef } from 'react';
import {
  FaTimes, FaMagic, FaSun, FaCloud, FaCar, FaPhoneAlt, FaCommentDots,
  FaArrowLeft, FaStop, FaExchangeAlt, FaCoins
} from 'react-icons/fa';

interface Props {
  regionSlug?: string;
}

const REGION_MAPPING = [
  { label: "Montréal (Métropole)", city: "Montreal", lat: 45.5017, lon: -73.5673 },
  { label: "Québec (Capitale)", city: "Quebec", lat: 46.8139, lon: -71.2080 },
  { label: "Gatineau (Outaouais)", city: "Gatineau", lat: 45.4765, lon: -75.7013 },
  { label: "Sherbrooke (Estrie)", city: "Sherbrooke", lat: 45.4001, lon: -71.8991 },
  { label: "Trois-Rivières (Mauricie)", city: "Trois-Rivieres", lat: 46.3427, lon: -72.5420 },
  { label: "Saguenay (Lac-St-Jean)", city: "Chicoutimi", lat: 48.4284, lon: -71.0700 },
  { label: "Rimouski (Bas-St-Laurent)", city: "Rimouski", lat: 48.4393, lon: -68.5238 },
  { label: "Gaspé (Gaspésie)", city: "Gaspe", lat: 48.8333, lon: -64.4833 },
  { label: "Sept-Îles (Côte-Nord)", city: "Sept-Iles", lat: 50.2001, lon: -66.3821 },
  { label: "Val-d'Or (Abitibi)", city: "Val-d'Or", lat: 48.0975, lon: -77.7828 },
  { label: "Drummondville (Centre-du-Qc)", city: "Drummondville", lat: 45.8833, lon: -72.4833 },
  { label: "Granby (Cantons-de-l'Est)", city: "Granby", lat: 45.4000, lon: -72.7333 },
  { label: "Saint-Hyacinthe (Montérégie)", city: "Saint-Hyacinthe", lat: 45.6333, lon: -72.9500 },
  { label: "Laval", city: "Laval", lat: 45.5833, lon: -73.7500 }
];

const ALL_REGIONS_NAMES = REGION_MAPPING.map(r => r.label);

const LANGUAGES = [
  { code: 'fr', label: 'FR', name: 'Français', currency: 'EUR', flag: "🇫🇷" },
  { code: 'en', label: 'EN', name: 'English', currency: 'USD', flag: "🇺🇸" },
  { code: 'es', label: 'ES', name: 'Español', currency: 'EUR', flag: "🇪🇸" },
  { code: 'it', label: 'IT', name: 'Italiano', currency: 'EUR', flag: "🇮🇹" },
  { code: 'de', label: 'DE', name: 'Deutsch', currency: 'EUR', flag: "🇩🇪" },
  { code: 'zh', label: 'ZH', name: 'Mandarin', currency: 'CNY', flag: "🇨🇳" }
];

const CITY_ACTIVITIES: Record<string, any> = {
  "Montreal": { sun: "Pique-nique au Mont-Royal.", cloud: "Vieux-Montréal.", rain: "Ville souterraine.", snow: "Patinage Vieux-Port.", storm: "Café du Plateau." },
  "Quebec": { sun: "Terrasse Dufferin.", cloud: "Petit-Champlain.", rain: "Musée de la Civilisation.", snow: "Glissade 1884.", storm: "Galeries de la Capitale." },
  "default": { sun: "Profitez des parcs locaux !", cloud: "Balade urbaine.", rain: "Musée ou magasinage.", snow: "Joies de l'hiver !", storm: "Restez au chaud." }
};

const QUIZ_CATEGORIES = ["Culture", "Géographie", "Expressions", "Histoire", "Faune & Flore"];

export default function ForfyLiveWidget({ regionSlug }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'live' | 'assistant' | 'quiz' | 'meteo' | 'jaser' | 'convert' | 'transport' | 'sos'>('live');
  const [lang, setLang] = useState<string>('fr');

  const [events, setEvents] = useState<any[]>([]);
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveCity, setLiveCity] = useState("Montreal");
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0];
  });

  const [weatherData, setWeatherData] = useState<any>(null);
  const [loadingWeather, setLoadingWeather] = useState(false);

  const [selectedRegion, setSelectedRegion] = useState(regionSlug || "Québec (Capitale)");
  const [displayedStory, setDisplayedStory] = useState("");
  const [loadingStory, setLoadingStory] = useState(false);

  const [displayedExpression, setDisplayedExpression] = useState("");
  const [loadingExpression, setLoadingExpression] = useState(false);

  const [cadAmount, setCadAmount] = useState<number>(100);
  const [convertedData, setConvertedData] = useState<any>(null);
  const [loadingConvert, setLoadingConvert] = useState(false);

  const [transportInfo, setTransportInfo] = useState<string | null>(null);
  const [displayedTransport, setDisplayedTransport] = useState("");
  const [loadingTransport, setLoadingTransport] = useState(false);

  const [quizCategory, setQuizCategory] = useState<string | null>(null);
  const [aiQuestion, setAiQuestion] = useState<any>(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState<'correct' | 'wrong' | null>(null);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const t: Record<string, any> = {
    tabs: {
      live: { fr: "LIVE", en: "LIVE" }, assistant: { fr: "HISTOIRES", en: "STORIES" }, jaser: { fr: "JASER", en: "CHAT" },
      meteo: { fr: "MÉTÉO", en: "WEATHER" }, convert: { fr: "DEVISES", en: "CURRENCY" },
      transport: { fr: "TRAJET", en: "ROUTE" }, sos: { fr: "AIDE", en: "HELP" }, quiz: { fr: "QUIZ", en: "QUIZ" }
    },
    ui: {
      city: { fr: "Région / Ville", en: "Region / City" },
      btnTickets: { fr: "VOIR BILLETS", en: "GET TICKETS" },
      btnGen: { fr: "Générer", en: "Generate" },
      placeholderStory: { fr: "Découvrez une anecdote locale...", en: "Discover a local story..." },
      placeholderJaser: { fr: "Expressions locales québécoises !", en: "Quebec local expressions!" },
      convertTitle: { fr: "Convertisseur de Devises", en: "Currency Converter" },
      convertFrom: { fr: "Montant (CAD)", en: "Amount (CAD)" },
      convertTo: { fr: "Vers", en: "To" },
      sosTitle: { fr: "Urgences & Santé", en: "Emergency & Health" },
      quizTitle: { fr: "Choisis une catégorie", en: "Pick a category" }, // ✅ ajouté (non-breaking)
    }
  };

  const getT = (group: string, key: string) =>
    (t[group] && t[group][key]) ? (t[group][key][lang] || t[group][key]['fr']) : key.toUpperCase();

  const getWeatherDetails = (code: number, city: string) => {
    let type = 'cloud';
    if (code === 0 || code === 1) type = 'sun';
    else if (code >= 51 && code <= 67) type = 'rain';
    else if (code >= 71 && code <= 77) type = 'snow';
    else if (code >= 80 && code <= 99) type = 'storm';

    const cityKey = Object.keys(CITY_ACTIVITIES).find(k => city.includes(k)) || "default";
    return {
      label: type === 'sun' ? "Dégagé" : "Nuageux",
      icon: type === 'sun' ? <FaSun className="text-yellow-400" /> : <FaCloud className="text-gray-400" />,
      rec: CITY_ACTIVITIES[cityKey][type]
    };
  };

  const typeText = (fullText: string, setter: (val: string) => void) => {
    let i = 0; setter("");
    const interval = setInterval(() => {
      setter(fullText.slice(0, i + 1)); i++;
      if (i >= fullText.length) clearInterval(interval);
    }, 20);
  };

  useEffect(() => {
    const handleOpenSmartHub = () => { setIsOpen(true); setActiveTab('assistant'); };
    window.addEventListener('open-smart-hub', handleOpenSmartHub);
    return () => window.removeEventListener('open-smart-hub', handleOpenSmartHub);
  }, []);

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.onended = () => setIsSpeaking(false);
  }, []);

  useEffect(() => {
    if (isOpen && (activeTab === 'live' || activeTab === 'meteo')) { loadLiveEvents(); loadWeather(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, liveCity, startDate, endDate]);

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsSpeaking(false);
  };

  const loadLiveEvents = async () => {
    setLoadingLive(true);
    try {
      const apiKey = import.meta.env.VITE_TICKETMASTER_API_KEY;
      const city = liveCity === 'Montreal' ? 'Montreal' : 'Quebec';
      const url = `https://app.ticketmaster.com/discovery/v2/events.json?apikey=${apiKey}&city=${city}&startDateTime=${startDate}T00:00:00Z&endDateTime=${endDate}T23:59:59Z&sort=date,asc&size=10`;
      const response = await fetch(url);
      const data = await response.json();
      setEvents(data._embedded?.events || []);
    } catch (e) { console.error(e); setEvents([]); } finally { setLoadingLive(false); }
  };

  const loadWeather = async () => {
    setLoadingWeather(true);
    try {
      const cityInfo = REGION_MAPPING.find(r => r.city === liveCity) || REGION_MAPPING[0];
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${cityInfo.lat}&longitude=${cityInfo.lon}&current=temperature_2m,weather_code,apparent_temperature,wind_speed_10m&timezone=America%2FNew_York`;
      const response = await fetch(url);
      const data = await response.json();
      setWeatherData(data.current);
    } catch (e) { console.error(e); } finally { setLoadingWeather(false); }
  };

  // ✅ IA via backend (plus d'OpenAI côté browser)
  const callAI = async (
    systemInstruction: string,
    userPrompt: string,
    finalSetter: (val: any) => void,
    displaySetter: (val: string) => void,
    loader: (val: boolean) => void,
    isJson = false,
    autoSpeak = false
  ) => {
    if (loader === setLoadingStory && loadingStory) return;
    if (loader === setLoadingExpression && loadingExpression) return;

    loader(true);
    displaySetter("");
    stopSpeaking();

    try {
      const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

      const resp = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          systemInstruction,
          userPrompt,
          languageName: currentLang.name,
          json: isJson,
          wantAudio: autoSpeak && !isJson,
          voice: 'onyx',
          maxTokens: isJson ? 280 : 220,
        })
      });

      const data = await resp.json();
      const content = data?.content || "";

      if (isJson) {
        // server renvoie data.data si parse OK
        finalSetter(data?.data ?? (() => {
          try { return JSON.parse(content); } catch { return null; }
        })());
      } else {
        typeText(content, displaySetter);
        if (autoSpeak && data?.audioUrl && audioRef.current) {
          audioRef.current.src = data.audioUrl;
          await audioRef.current.play().catch(() => {});
          setIsSpeaking(true);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      loader(false);
    }
  };

  const generateStory = () =>
    callAI(
      "You are an expert Quebec guide. Tell a short fascinating legend or fact.",
      `Tell me about ${selectedRegion}. Max 60 words.`,
      () => {},
      setDisplayedStory,
      setLoadingStory,
      false,
      true
    );

  const generateExpression = () =>
    callAI(
      "You are a Quebec linguistic expert.",
      "Give me a funny Quebec expression with its definition.",
      () => {},
      setDisplayedExpression,
      setLoadingExpression,
      false,
      true
    );

  const handleConvert = () => {
    const targetCurrency = LANGUAGES.find(l => l.code === lang)?.currency || 'EUR';
    callAI(
      "Finance Expert.",
      `Convert ${cadAmount} CAD to ${targetCurrency}. Output JSON: {result: "X CUR", note: "Short tax tip for tourists in Quebec"}.`,
      setConvertedData,
      () => {},
      setLoadingConvert,
      true,
      false
    );
  };

  const handleTransport = () =>
    callAI(
      "Local Guide.",
      `Give transport advice for ${liveCity}. Max 40 words.`,
      setTransportInfo,
      setDisplayedTransport,
      setLoadingTransport,
      false,
      false
    );

  const generateQuizQuestion = (cat: string) => {
    setQuizResult(null);
    callAI(
      "Quiz Master.",
      `Generate a multiple choice question about Quebec ${cat}. JSON format: {question: "str", options: ["str"], correctIndex: int}`,
      setAiQuestion,
      () => {},
      setLoadingQuiz,
      true,
      false
    );
  };

  const handleQuizResponse = (idx: number) => {
    if (idx === aiQuestion.correctIndex) setQuizResult('correct');
    else setQuizResult('wrong');
  };

  return (
    <div style={{ position: 'fixed', bottom: '16px', right: '16px', zIndex: 99999, pointerEvents: 'none' }} className="font-sans flex flex-col items-end">
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[95vw] max-w-[450px] max-h-[80vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 duration-300 pointer-events-auto mb-4">

          {/* Header */}
          <div className="bg-slate-900 p-2 flex items-center justify-between text-white shrink-0">
            <div className="flex gap-1 overflow-x-auto no-scrollbar flex-1 items-center py-1 pr-2">
              <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-slate-800 text-[10px] font-bold rounded-md px-1.5 py-1.5 border border-slate-700 outline-none shrink-0">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>

              {['live', 'assistant', 'jaser', 'meteo', 'convert', 'transport', 'sos', 'quiz'].map(tCode => (
                <button key={tCode} onClick={() => setActiveTab(tCode as any)} className={`px-3 py-1.5 text-[10px] font-bold rounded-full whitespace-nowrap transition ${activeTab === tCode ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}>
                  {getT('tabs', tCode)}
                </button>
              ))}
            </div>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 px-2 shrink-0"><FaTimes size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 min-h-0 text-left">

            {/* Live */}
            {activeTab === 'live' && (
              <div className="p-3 text-left">
                <label className="text-[9px] font-bold text-slate-400 uppercase mb-1 block">{getT('ui', 'city')}</label>
                <select value={liveCity} onChange={(e) => setLiveCity(e.target.value)} className="w-full border p-2 rounded-lg text-xs mb-3 outline-none">
                  {REGION_MAPPING.map(r => <option key={r.city} value={r.city}>{r.label}</option>)}
                </select>
                <div className="space-y-3">
                  {loadingLive ? <div className="text-center py-10 animate-pulse text-slate-400">Chargement...</div> : events.map(evt => (
                    <div key={evt.id} className="bg-white border rounded-xl overflow-hidden shadow-sm flex flex-col">
                      <div className="h-24 bg-slate-100"><img src={evt.images?.[0]?.url} className="w-full h-full object-cover" alt="event" /></div>
                      <div className="p-2 flex flex-col text-left">
                        <h4 className="font-bold text-xs line-clamp-1">{evt.name}</h4>
                        <a href={evt.url} target="_blank" rel="noreferrer" className="block w-full mt-2 text-center py-1.5 bg-orange-500 text-white rounded-md text-[10px] font-bold">
                          {getT('ui', 'btnTickets')}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Météo */}
            {activeTab === 'meteo' && (
              <div className="p-4 bg-sky-50 h-full text-left">
                <select value={liveCity} onChange={(e) => setLiveCity(e.target.value)} className="w-full border p-2 rounded-lg text-xs mb-4 outline-none">
                  {REGION_MAPPING.map(r => <option key={r.city} value={r.city}>{r.label}</option>)}
                </select>
                {weatherData ? (
                  <div className="bg-white p-6 rounded-3xl shadow-lg border border-sky-100 text-center animate-in zoom-in duration-300">
                    <div className="text-6xl mb-4">{getWeatherDetails(weatherData.weather_code, liveCity).icon}</div>
                    <div className="text-5xl font-black text-slate-800">{Math.round(weatherData.temperature_2m)}°c</div>
                    <div className="flex justify-between border-t mt-4 pt-4 text-[10px] font-bold text-slate-500">
                      <span>💨 {weatherData.wind_speed_10m} km/h</span>
                      <span>🌡️ {Math.round(weatherData.apparent_temperature)}°</span>
                    </div>
                  </div>
                ) : <div className="text-center py-20 text-slate-400">Chargement...</div>}
              </div>
            )}

            {/* Transport */}
            {activeTab === 'transport' && (
              <div className="p-4 flex flex-col h-full bg-indigo-50 text-left">
                <h3 className="text-sm font-black text-indigo-800 mb-4 uppercase">{getT('tabs', 'transport')}</h3>
                <div className="flex-1 bg-white p-4 rounded-xl border min-h-[150px] mb-4 text-sm italic text-slate-700 shadow-inner">
                  {loadingTransport ? <FaCar className="animate-bounce text-indigo-400 text-3xl mx-auto my-10" /> : <p>{displayedTransport || `Transport à ${liveCity}...`}</p>}
                </div>
                <button onClick={handleTransport} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition">
                  <FaCar /> {getT('ui', 'btnGen')}
                </button>
              </div>
            )}

            {/* SOS */}
            {activeTab === 'sos' && (
              <div className="p-4 flex flex-col h-full bg-red-50 text-left">
                <h3 className="text-sm font-black text-red-800 mb-4 uppercase">{getT('ui', 'sosTitle')}</h3>
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded-xl border-l-4 border-red-500 shadow-sm flex items-center justify-between text-left">
                    <div><p className="text-xs font-bold text-slate-400">9-1-1</p><p className="text-xl font-black text-red-600">Police/Urgences</p></div>
                    <FaPhoneAlt className="text-red-500 text-xl" />
                  </div>
                  <div className="bg-white p-4 rounded-xl border-l-4 border-blue-500 shadow-sm text-left">
                    <div><p className="text-xs font-bold text-slate-400">8-1-1</p><p className="text-lg font-black text-blue-600">Info-Santé</p></div>
                  </div>
                </div>
              </div>
            )}

            {/* Quiz */}
            {activeTab === 'quiz' && (
              <div className="p-4 bg-purple-50 h-full flex flex-col items-center text-left">
                {!quizCategory ? (
                  <div className="grid grid-cols-1 gap-3 w-full">
                    <h3 className="text-sm font-black text-purple-800 mb-2 uppercase text-left">{getT('ui', 'quizTitle')}</h3>
                    {QUIZ_CATEGORIES.map(cat => (
                      <button key={cat} onClick={() => { setQuizCategory(cat); generateQuizQuestion(cat); }} className="bg-white p-3 rounded-xl border-2 border-purple-100 font-bold text-slate-700 text-sm hover:border-purple-500 transition shadow-sm text-left">
                        {cat}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="w-full animate-in slide-in-from-right duration-300">
                    <button onClick={() => setQuizCategory(null)} className="mb-4 text-purple-600 flex items-center gap-1 text-xs font-bold"><FaArrowLeft /> Retour</button>
                    {loadingQuiz ? <div className="text-center py-10 animate-spin"><FaMagic className="text-purple-400 text-2xl mx-auto" /></div> : aiQuestion && (
                      <div className="space-y-4 text-left">
                        <h4 className="font-bold text-slate-800 text-sm leading-snug text-left">{aiQuestion.question}</h4>
                        <div className="space-y-2">
                          {aiQuestion.options.map((opt: any, i: number) => (
                            <button
                              key={i}
                              onClick={() => handleQuizResponse(i)}
                              disabled={quizResult !== null}
                              className={`w-full p-2.5 rounded-xl border-2 text-xs font-bold transition-all text-left ${quizResult ? (i === aiQuestion.correctIndex ? 'bg-green-100 border-green-500 text-green-800' : 'bg-white text-slate-300') : 'bg-white border-purple-100'}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                        {quizResult && <button onClick={() => generateQuizQuestion(quizCategory)} className="w-full bg-purple-600 text-white p-2.5 rounded-xl font-bold text-xs shadow-lg uppercase text-center">Suivante 👉</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* HISTOIRES */}
            {activeTab === 'assistant' && (
              <div className="p-4 flex flex-col h-full text-left">
                <h3 className="text-sm font-black text-blue-800 mb-2 uppercase">{getT('tabs', 'assistant')}</h3>
                <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold mb-3 shadow-sm outline-none">
                  {ALL_REGIONS_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="flex-1 bg-white p-4 rounded-xl border border-slate-100 italic text-sm text-slate-700 min-h-[120px] mb-3">
                  {loadingStory ? <div className="flex justify-center items-center h-full"><FaMagic className="animate-spin text-blue-500 text-2xl" /></div> : <p>{displayedStory || getT('ui', 'placeholderStory')}</p>}
                </div>
                <button
                  onClick={generateStory}
                  disabled={loadingStory}
                  className={`w-full text-white py-3 rounded-xl text-xs font-bold shadow-lg flex items-center justify-center gap-2 transition text-center ${loadingStory ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {loadingStory ? <FaMagic className="animate-spin" /> : <FaMagic />}
                  {loadingStory ? 'Génération...' : getT('ui', 'btnGen')}
                </button>
                {isSpeaking && <button onClick={stopSpeaking} className="mt-2 text-blue-500 text-xs font-bold flex items-center justify-center gap-1"><FaStop /> Arrêter l'audio</button>}
              </div>
            )}

            {/* JASER */}
            {activeTab === 'jaser' && (
              <div className="p-4 flex flex-col h-full items-center text-left">
                <h3 className="text-sm font-black text-teal-800 mb-2 uppercase">{getT('tabs', 'jaser')} !</h3>
                <div className="w-full flex-1 bg-white p-5 rounded-2xl border border-teal-100 mb-4 flex items-center justify-center text-center italic text-slate-800 font-medium min-h-[120px]">
                  {loadingExpression ? <FaCommentDots className="animate-bounce text-teal-400 text-2xl" /> : <p>{displayedExpression || getT('ui', 'placeholderJaser')}</p>}
                </div>
                <button
                  onClick={generateExpression}
                  disabled={loadingExpression}
                  className={`w-full text-white py-3 rounded-xl text-xs font-bold shadow-lg flex items-center justify-center gap-2 ${loadingExpression ? 'bg-teal-400 cursor-not-allowed' : 'bg-teal-600'}`}
                >
                  {loadingExpression ? <FaCommentDots className="animate-spin" /> : <FaCommentDots />}
                  {getT('ui', 'btnGen')}
                </button>
                {isSpeaking && <button onClick={stopSpeaking} className="mt-2 text-teal-500 text-xs font-bold flex items-center justify-center gap-1"><FaStop /> Arrêter l'audio</button>}
              </div>
            )}

            {/* CONVERT */}
            {activeTab === 'convert' && (
              <div className="p-4 flex flex-col h-full bg-emerald-50 text-left">
                <h3 className="text-sm font-black text-emerald-800 mb-4 uppercase flex items-center gap-2">
                  <FaCoins className="text-emerald-600" /> {getT('ui', 'convertTitle')}
                </h3>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-emerald-100 mb-4">
                  <div className="mb-4">
                    <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-wider">{getT('ui', 'convertFrom')}</label>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                      <span className="text-xl">🇨🇦</span>
                      <input
                        type="number"
                        value={cadAmount}
                        onChange={(e) => setCadAmount(Number(e.target.value))}
                        className="w-full bg-transparent border-none text-xl font-black text-slate-800 outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center -my-3 relative z-10">
                    <div className="bg-emerald-100 p-1.5 rounded-full text-emerald-600"><FaExchangeAlt size={12} /></div>
                  </div>

                  <div className="mt-2 mb-6">
                    <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase tracking-wider">
                      {getT('ui', 'convertTo')} {LANGUAGES.find(l => l.code === lang)?.currency}
                    </label>
                    <div className="flex items-center gap-2 bg-emerald-50 p-2 rounded-lg border border-emerald-200">
                      <span className="text-xl">{LANGUAGES.find(l => l.code === lang)?.flag}</span>
                      <div className="text-xl font-black text-emerald-800">
                        {convertedData ? String(convertedData.result || '').split(' ')[0] : '...'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleConvert}
                    disabled={loadingConvert}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold text-xs shadow-lg uppercase text-center transition flex justify-center items-center gap-2"
                  >
                    {loadingConvert ? <FaExchangeAlt className="animate-spin" /> : <FaExchangeAlt />}
                    {getT('ui', 'btnGen')}
                  </button>
                </div>

                {convertedData && (
                  <div className="bg-white p-3 rounded-xl border border-emerald-100 text-[10px] text-slate-500 italic">
                    💡 {convertedData.note}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!isOpen && (
        <div className="flex flex-col items-end gap-2 pointer-events-auto cursor-pointer" onClick={() => setIsOpen(true)}>
          <div className="bg-white px-3 py-1.5 rounded-xl rounded-br-none shadow-md border border-slate-100 animate-bounce-slow text-[10px] font-bold text-slate-700 italic">
            Smart Hub Forfy 👋
          </div>
          <button className="w-16 h-16 rounded-full border-4 border-white shadow-2xl overflow-hidden hover:scale-105 transition-transform">
            <video src="/GifForfy.MP4" autoPlay loop muted playsInline className="w-full h-full object-cover" />
          </button>
        </div>
      )}
      <audio ref={audioRef} hidden />
    </div>
  );
}
